import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CelebrationHeader from "./_components/CelebrationHeader";
import GiftBoxReveal from "./_components/GiftBoxReveal";
import PuzzleCompletion from "./_components/PuzzleCompletion";
import RocketDelivery from "./_components/RocketDelivery";
import ToyFactory from "./_components/ToyFactory";
import ToyShelf from "./_components/ToyShelf";

type Stage =
  | "tasks"
  | "merge"
  | "factory"
  | "gift"
  | "rocket"
  | "shelf"
  | "celebrate";

const buttonClass =
  "rounded-full px-6 py-2 text-sm font-semibold text-white shadow transition-transform hover:scale-105";

const AnimationPage = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("tasks");
  const [completedTasks, setCompletedTasks] = useState([false, false, false]);
  const [merged, setMerged] = useState(false);

  useEffect(() => {
    if (stage !== "tasks") return;
    const timers = [
      setTimeout(() => setCompletedTasks([true, false, false]), 800),
      setTimeout(() => setCompletedTasks([true, true, false]), 1600),
      setTimeout(() => setCompletedTasks([true, true, true]), 2400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [stage]);

  const handleMergeComplete = useCallback(() => {
    setStage("factory");
  }, []);

  useEffect(() => {
    if (completedTasks.every(Boolean) && stage === "tasks") {
      const timer = setTimeout(() => {
        setMerged(true);
        setStage("merge");
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [completedTasks, stage]);

  const handleReset = () => {
    setStage("tasks");
    setCompletedTasks([false, false, false]);
    setMerged(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-orange-50 to-rose-100 p-4">
      <div className="mx-auto flex min-h-screen w-full max-w-2xl items-center justify-center">
        <motion.div
          className="w-full overflow-hidden rounded-3xl border border-white/60 bg-white/90 p-6 shadow-2xl md:p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <CelebrationHeader visible={stage === "celebrate"} />

          <div className="flex min-h-[340px] flex-col items-center justify-center gap-6">
            <AnimatePresence mode="wait">
              {(stage === "tasks" || stage === "merge") && (
                <motion.div key="puzzle" exit={{ opacity: 0, scale: 0.8 }}>
                  <PuzzleCompletion
                    completedTasks={completedTasks}
                    merged={merged}
                    onMergeComplete={handleMergeComplete}
                  />
                </motion.div>
              )}

              {stage === "factory" && (
                <motion.div key="factory" exit={{ opacity: 0, y: -20 }}>
                  <ToyFactory
                    active
                    onComplete={() => setTimeout(() => setStage("gift"), 500)}
                  />
                </motion.div>
              )}

              {stage === "gift" && (
                <motion.div key="gift" exit={{ opacity: 0, scale: 0.5 }}>
                  <GiftBoxReveal active onToyRevealed={() => setStage("rocket")} />
                </motion.div>
              )}

              {stage === "rocket" && (
                <motion.div key="rocket" exit={{ opacity: 0 }}>
                  <RocketDelivery active onLanded={() => setStage("shelf")} />
                </motion.div>
              )}

              {(stage === "shelf" || stage === "celebrate") && (
                <motion.div key="shelf" className="w-full">
                  <ToyShelf
                    active={stage === "shelf" || stage === "celebrate"}
                    onPlaced={() => setTimeout(() => setStage("celebrate"), 600)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence mode="wait">
            <motion.p
              key={stage}
              className="mt-4 text-center text-sm font-bold text-slate-600"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
            >
              {stage === "tasks" && "Completing tasks..."}
              {stage === "merge" && "Combining your achievements!"}
              {stage === "factory" && "Making your toy..."}
              {stage === "gift" && "What could it be?"}
              {stage === "rocket" && "Delivering your toy!"}
              {stage === "shelf" && "Adding to your collection..."}
              {stage === "celebrate" && ""}
            </motion.p>
          </AnimatePresence>

          {stage === "celebrate" && (
            <motion.div
              className="mt-6 flex justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <button
                type="button"
                className={`${buttonClass} bg-blue-600`}
                onClick={handleReset}
              >
                Try Again
              </button>
              <button
                type="button"
                className={`${buttonClass} bg-emerald-600`}
                onClick={() => navigate("/word-prompt")}
              >
                Next Word
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AnimationPage;
